import { useMemo, useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import FormSelect from 'components/form/FormSelect'
import { useTranslation } from 'react-i18next'
import { Table } from 'reactstrap'
import { flexRender } from '@tanstack/react-table'
import { ChevronUp, ChevronDown } from 'react-feather'
import { TanstackContext } from '../TanstackContext'
import TanstackPaginator from '../TanstackPaginator'

const SortTableSelectColumns = ({ table, onSortingChange }) => {
  SortTableSelectColumns.propTypes = {
    table: PropTypes.object,
    onSortingChange: PropTypes.func,
  }

  const [sortDesc, setSortDesc] = useState(false)
  const { t } = useTranslation()

  const sortOptions = useMemo(() => {
    return table.getVisibleFlatColumns().map((column) => {
      const { id, columnDef } = column
      return { label: columnDef.header, value: id }
    })
  }, [table])

  const sortColumn = table.getState().sorting[0] || { id: '', desc: true }

  const handleSelectSortChange = (option) => {
    onSortingChange([{ id: option.value, desc: sortDesc }])
  }

  useEffect(() => {
    if (sortColumn.id) {
      handleSelectSortChange({ value: sortColumn.id })
    }
  }, [sortDesc])

  return (
    <div className="d-flex align-items-center gap-3">
      <div className="w-100">
        <FormSelect
          label={t('sort')}
          options={sortOptions}
          onChange={handleSelectSortChange}
          value={sortOptions.find((op) => op.value === sortColumn.id)}
        />
      </div>
      <div className="mt-3">
        <SortIconButton desc={sortDesc} toggleFunc={() => setSortDesc((prev) => !prev)} />
      </div>
    </div>
  )
}

const SortIconButton = ({ desc, toggleFunc }) => {
  SortIconButton.propTypes = {
    desc: PropTypes.bool,
    toggleFunc: PropTypes.func,
  }
  const sortDesc = 'bi bi-sort-down-alt h1'
  const sortAsc = 'bi bi-sort-up-alt h1'
  const className = desc ? sortDesc : sortAsc
  return <i className={className} role="button" onClick={() => toggleFunc()} tabIndex={0} />
}

const TableColumns = () => {
  const { table, sortingCols, filtering } = useContext(TanstackContext)

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <Table bordered hover striped>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} onClick={header.column.getToggleSortingHandler()}>
                    {header.isPlaceholder ? null : (
                      <div>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {
                          { asc: <ChevronUp />, desc: <ChevronDown /> }[
                            header.column.getIsSorted() ?? null
                          ]
                        }
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <TanstackPaginator />
    </div>
  )
}

export default TableColumns
